import request from '@/services/base-service';
import { TableTanSuat } from '@/types/performance/buoc5';
import type { ModelViewTarget } from '@/types/performance/buoc6phuongdien';
import type { CoCauHistoryStrategyMapRequest } from '@/types/performance/buoc4congty';
import { RequestDTO } from '@/types/requestDTO';
import { EditChiTieuView } from '@/types/performance/edtichitieuviewv2';
import { InternalAxiosRequestConfig } from 'axios';
import { AccessView } from '@/types/performance/AccessView';
import { RequestSDK } from '@/types/sdk/requestSDK';
import { TargetDetailRequest } from '@/types/performance/Target/TargetDetailRequest';
import { ModelEditTarget } from '@/types/performance/Target/EditModelTarget';
import type { SyncChiTieuChildrenPreview } from '@/types/performance/Target/EditModelTarget';
import { TargetFishboneDetailRequest } from '@/types/performance/Target/TargetFishboneDetailRequest';
import { KpiDefinitionDTO } from '@/types/performance/Target/LayDinhNghiaView';
import { InfoTarget } from '@/types/performance/edtichitieuviewv2';
import { ApiResponse } from '@/types/base';
import type { ChiTieuChildWarningResponse } from '@/types/performance/Target/ChiTieuChildWarning';
import type { SaveChiTieuChildWarnDismissRequest } from '@/types/performance/Target/SaveChiTieuChildWarnDismiss';

const baseUrl = '/v1/performance';
// const baseUrlServer = '/v1/performance';
// Path tương đối qua axios baseURL không tự resolve đúng host ở môi trường dev này (cùng nguyên nhân
// đã gặp ở fishbone-api.ts/co-cau-giai-doan-api.ts/strategy-map-company-api.ts) — hardcode tuyệt đối cho endpoint mới.
export const buoc6Api = {
    /**
     * Get targets data for buoc6
     * @param requestDTO - Request parameters including year and selectTree
     * @returns Promise with targets data
     */
    getTargets: (requestDTO: RequestDTO) =>
        request.post<ModelViewTarget>(
            `${baseUrl}/gettargets`,
            requestDTO,
            { authorization: true } as InternalAxiosRequestConfig
        ),

    /** Đo lường & chỉ tiêu (Bước 6) theo lịch sử cơ cấu — đọc snapshot "ngắt hiệu suất", không phải dữ liệu sống. */
    getTargetsCoCauHistory: (requestDTO: CoCauHistoryStrategyMapRequest) =>
        request.post(
            `${baseUrl}/gettargets-cocau-history`,
            requestDTO,
            { authorization: true } as InternalAxiosRequestConfig
        ),
    /**
     * Get target detail V2 - returns list of InfoTarget for different periods
     * @param detailRequest - Target detail request parameters
     * @returns Promise with list of InfoTarget data
     */
    getTargetDetailV2: (detailRequest: TargetDetailRequest) =>
        request.post<InfoTarget[]>(
            `${baseUrl}/gettargetdetail`,
            detailRequest,
            { authorization: true } as InternalAxiosRequestConfig
        ),

    /**
     * Get timeline data
     * @param model - Request parameters including DateChoice
     * @returns Promise with timeline data
     */
    getTimeLine: (model: RequestDTO) =>
        request.post<TableTanSuat[]>(
            `${baseUrl}/gettimeline`,
            model,
            { authorization: true } as InternalAxiosRequestConfig
        ),

    /**
     * Get all KPI definitions
     * @param model - Request parameters including ID
     * @returns Promise with KPI definitions data
     */
    getAllKPIDefinitions: (model: RequestDTO) =>
        request.post<KpiDefinitionDTO>(
            `${baseUrl}/getallkpidefinitions`,
            model,
            { authorization: true } as InternalAxiosRequestConfig
        ),

    /**
     * Get list KPI chi tieu by list phu trach ao
     * @param model - Request parameters including Year and ListID
     * @returns Promise with access view data
     */
    getListKPIChiTieuByListPhuTrachAo: (model: RequestSDK) =>
        request.post<AccessView>(
            `${baseUrl}/getlistkpichitieubylistphutrachao`,
            model,
            { authorization: true } as InternalAxiosRequestConfig
        ),
    /**
     * Get AccessView (ListMucTieus, ListKPIs, ...) for current user / IDPhuTrach / Year
     */
    /** Chỉ tiêu theo danh sách ID — `HasGuid: false` lấy cả chỉ tiêu đã lưu trữ (Guid) / không còn trong AccessView. */
    getListTargetByIds: (model: { ListID: string[]; HasGuid: boolean }) =>
        request.post<ApiResponse<AccessView['ListChiTieus']>>(
            `${baseUrl}/getlisttargetbyids`,
            model,
            { authorization: true } as InternalAxiosRequestConfig
        ),
    getAccessViewAll: (model: RequestSDK) =>
        request.post<ApiResponse<AccessView>>(
            `${baseUrl}/getaccessviewall`,
            model,
            { authorization: true } as InternalAxiosRequestConfig
        ),
    getAccessView: (model: RequestSDK) =>
        request.post<ApiResponse<AccessView>>(
            `${baseUrl}/getaccessview`,
            model,
            { authorization: true } as InternalAxiosRequestConfig
        ),
    save: (model: ModelEditTarget) =>
        request.post<boolean>(
            `${baseUrl}/save`,
            model,
            { authorization: true } as InternalAxiosRequestConfig
        ),

    previewSyncChiTieuChildren: (model: ModelEditTarget) =>
        request.post<SyncChiTieuChildrenPreview>(
            `${baseUrl}/syncchitieuchildrenpreview`,
            model,
            { authorization: true } as InternalAxiosRequestConfig
        ),

    saveChiTieuChildWarnDismiss: (model: SaveChiTieuChildWarnDismissRequest) =>
        request.post<{ dismissed: boolean }>(
            `${baseUrl}/savechitieuchildwarndismiss`,
            model,
            { authorization: true } as InternalAxiosRequestConfig
        ),

    getChiTieuChildWarnings: (model: RequestDTO) =>
        request.post<ChiTieuChildWarningResponse>(
            `${baseUrl}/getchitieuchildwarnings`,
            model,
            { authorization: true } as InternalAxiosRequestConfig
        ),

    /**
     * Save chi tiet KPI
     * @param model - EditChiTieuView data
     * @returns Promise with save result
     */
    saveChiTiet: (model: EditChiTieuView) =>
        request.post<unknown>(
            `${baseUrl}/savechitiet`,
            model,
            { authorization: true } as InternalAxiosRequestConfig
        ),

    targetFishboneDetail: (model: TargetFishboneDetailRequest) =>
        request.post(
            `${baseUrl}/getkpichild`,
            model,
            { authorization: true } as InternalAxiosRequestConfig
        ),

    /** Xem xương cá (modal read-only) — GetModalXuongCa */
    getModalXuongCa: (model: TargetFishboneDetailRequest) =>
        request.post(
            `${baseUrl}/getmodalxuongca`,
            model,
            { authorization: true } as InternalAxiosRequestConfig
        ),

    saveKPIChild: (model: RequestDTO) =>
        request.post<boolean>(
            `${baseUrl}/savekpichild`,
            model,
            { authorization: true } as InternalAxiosRequestConfig
        ),

    exportTargets: (model: RequestDTO) =>
        request.post<ApiResponse<string[]>>(
            `${baseUrl}/exporttargets`,
            model,
            {
                authorization: true,
            } as InternalAxiosRequestConfig
        ),

    exportSampleTargets: (model: RequestDTO) =>
        request.post(
            `${baseUrl}/exportsampletargets`,
            model,
            {
                authorization: true,
                responseType: 'blob',
            } as InternalAxiosRequestConfig
        ),

    downloadExportTargets: (model: RequestDTO) =>
        request.post<string>(
            `${baseUrl}/downloadexporttargets`,
            model,
            {
                authorization: true,
            } as InternalAxiosRequestConfig
        ),

    importTargets: (
        file: File,
        model: RequestDTO & {
            allBoPhan?: boolean;
            allNhanVien?: boolean;
            listBoPhan?: string[];
            listNhanVien?: string[];
        }
    ) => {
        const formData = new FormData();
        formData.append('file', file);
        // ASP.NET Core model binder reads RequestDTO from multipart form fields
        if (model?.tanSuat !== undefined && model?.tanSuat !== null) formData.append('tanSuat', String(model.tanSuat));
        if (model?.showKPI !== undefined && model?.showKPI !== null) formData.append('showKPI', String(model.showKPI));
        if (model?.dateChoice !== undefined && model?.dateChoice !== null) formData.append('dateChoice', String(model.dateChoice));
        if (model?.allBoPhan !== undefined && model?.allBoPhan !== null) formData.append('allBoPhan', String(model.allBoPhan));
        if (model?.allNhanVien !== undefined && model?.allNhanVien !== null) formData.append('allNhanVien', String(model.allNhanVien));

        // RequestHeader (minimum for permission check: selectTree.idSelected)
        if (model?.requestHeader?.phanHe) formData.append('requestHeader.phanHe', String(model.requestHeader.phanHe));
        if (model?.requestHeader?.tenantID) formData.append('requestHeader.tenantID', String(model.requestHeader.tenantID));
        if (model?.requestHeader?.selectTree?.idSelected !== undefined && model?.requestHeader?.selectTree?.idSelected !== null) {
            formData.append('requestHeader.selectTree.idSelected', String(model.requestHeader.selectTree.idSelected));
        }
        if (model?.requestHeader?.selectTree?.idParentSelected) formData.append('requestHeader.selectTree.idParentSelected', String(model.requestHeader.selectTree.idParentSelected));
        if (model?.requestHeader?.selectTree?.isEmployee !== undefined && model?.requestHeader?.selectTree?.isEmployee !== null) {
            formData.append('requestHeader.selectTree.isEmployee', String(model.requestHeader.selectTree.isEmployee));
        }
        if (model?.requestHeader?.selectTree?.isCompany !== undefined && model?.requestHeader?.selectTree?.isCompany !== null) {
            formData.append('requestHeader.selectTree.isCompany', String(model.requestHeader.selectTree.isCompany));
        }
        if (model?.requestHeader?.selectPeriod?.idGiaiDoan) formData.append('requestHeader.selectPeriod.idGiaiDoan', String(model.requestHeader.selectPeriod.idGiaiDoan));
        if (model?.requestHeader?.selectPeriod?.year !== undefined && model?.requestHeader?.selectPeriod?.year !== null) {
            formData.append('requestHeader.selectPeriod.year', String(model.requestHeader.selectPeriod.year));
        }
        if (model?.requestHeader?.selectPeriod?.dateChoice) formData.append('requestHeader.selectPeriod.dateChoice', String(model.requestHeader.selectPeriod.dateChoice));

        // Lists
        if (Array.isArray(model?.listBoPhan)) model.listBoPhan.forEach((v: string) => formData.append('listBoPhan', String(v)));
        if (Array.isArray(model?.listNhanVien)) model.listNhanVien.forEach((v: string) => formData.append('listNhanVien', String(v)));

        return request.post(`${baseUrl}/importtargets`, formData, {
            authorization: true,
            // Do NOT set Content-Type manually; browser/axios will attach correct boundary
        } as InternalAxiosRequestConfig);
    },

    /**
     * Danh sách mục tiêu phân cấp lazy-load theo ParentID.
     * parentId rỗng / null / '0' = mục tiêu gốc; có giá trị = load con.
     * idPhuTrach chỉ filter root; load con không truyền / bỏ qua.
     */
    getListMucTieuHierarchy: (parentId?: string | null, idPhuTrach?: string | null) =>
        request.post(
            `${baseUrl}/getlistmuctieuhierarchy`,
            {
                parentId: parentId ?? '',
                idPhuTrach: idPhuTrach ?? '',
            },
            { authorization: true } as InternalAxiosRequestConfig
        ),
};
